import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const HomeContainer = styled.div`
  min-height: 100vh;
  padding-top: 80px;
  background: ${props => props.theme.colors.background};
`;

const HeroSection = styled.section`
  height: 100vh;
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  background-image: url('https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1920&h=1080&fit=crop');
  background-size: cover;
  background-position: center;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 0;
  }
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    height: 80vh;
  }
`;

const HeroContent = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
`;

const VerticalSchurr = styled.div`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  writing-mode: vertical-rl;
  text-orientation: mixed;

  h1 {
    font-family: ${props => props.theme.fonts.primary};
    font-size: clamp(8rem, 16vw, 20rem);
    font-weight: 900;
    color: #5F8D4E;
    margin: 0;
    letter-spacing: -0.04em;
    text-transform: uppercase;
    line-height: 0.85;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    left: 15px;

    h1 {
      font-size: clamp(5rem, 12vw, 10rem);
    }
  }
`;

const HorizontalJournalism = styled.div`
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);

  h2 {
    font-family: ${props => props.theme.fonts.primary};
    font-size: clamp(5rem, 10vw, 16rem);
    font-weight: 900;
    color: ${props => props.theme.colors.text};
    margin: 0;
    letter-spacing: -0.03em;
    text-transform: uppercase;
    line-height: 0.75;
    text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.8);
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    bottom: 40px;

    h2 {
      font-size: clamp(2.5rem, 7vw, 7rem);
    }
  }
`;

const VerticalNav = styled.nav`
  position: absolute;
  right: 20px;
  top: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    right: 15px;
    gap: ${props => props.theme.spacing.md};
  }
`;

const VerticalNavLink = styled(Link)`
  font-family: ${props => props.theme.fonts.primary};
  font-size: 0.7rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 2px;
  padding: 0;
  transition: all ${props => props.theme.transitions.normal};
  position: relative;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    font-size: 0.65rem;
  }
`;

const AdminNavLink = styled(VerticalNavLink)`
  color: ${props => props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.base};
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.background};
  }
`;

const SpartanWeeklySection = styled.section`
  padding: ${props => props.theme.spacing.huge} 0;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
  position: relative;
  z-index: 1;

  h2 {
    font-size: clamp(3rem, 8vw, 8rem);
    font-weight: 900;
    color: #5F8D4E;
    margin-bottom: 0;
    letter-spacing: -0.02em;
    text-transform: uppercase;
    line-height: 0.8;

    &:first-child {
      color: #5F8D4E;
    }

    span {
      color: ${props => props.theme.colors.text};
    }
  }

  p {
    display: none;
  }
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  position: relative;
  z-index: 1;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const MainFeatured = styled.article`
  background: ${props => props.theme.colors.surface};
  border-radius: 0;
  overflow: hidden;
  transition: all ${props => props.theme.transitions.normal};
  position: relative;

  .featured-image {
    height: 500px;
    background-image: url(${props => props.image});
    background-size: cover;
    background-position: center;
    position: relative;

    .category-badge {
      position: absolute;
      top: ${props => props.theme.spacing.lg};
      left: ${props => props.theme.spacing.lg};
      background: ${props => props.theme.colors.primary};
      color: ${props => props.theme.colors.text};
      padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
      font-size: 0.75rem;
      font-weight: 700;
      border-radius: 0;
      text-transform: uppercase;
      letter-spacing: 2px;
    }
  }

  .featured-content {
    padding: ${props => props.theme.spacing.xxl};

    h3 {
      font-size: 2.5rem;
      font-weight: 900;
      line-height: 1.1;
      color: ${props => props.theme.colors.text};
      margin-bottom: ${props => props.theme.spacing.md};
      text-transform: none;
    }

    p {
      font-size: 1rem;
      line-height: 1.6;
      color: ${props => props.theme.colors.textSecondary};
      margin-bottom: 0;
      font-weight: 400;
    }
  }
`;

const VideoSection = styled.div`
  background: #E5E5E5;
  border-radius: 0;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 400px;

  .video-label {
    font-size: 2.5rem;
    font-weight: 300;
    color: ${props => props.theme.colors.background};
    text-align: center;
  }
`;

const GallerySection = styled.section`
  padding: ${props => props.theme.spacing.huge} 0;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;

  ${SectionHeader} h2 {
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    font-size: clamp(4rem, 10vw, 12rem);
    z-index: 2;
  }
`;

const GalleryGrid = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 1;
  padding: 0 100px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    padding: 0 20px;
  }
`;

const GalleryArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 80px;
  height: 80px;
  background: ${props => props.theme.colors.text};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: ${props => props.theme.colors.background};
  z-index: 10;
  transition: all ${props => props.theme.transitions.normal};

  &:hover {
    background: ${props => props.theme.colors.primary};
  }

  &.left {
    left: 0;
  }

  &.right {
    right: 0;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const GalleryItem = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 0;
  overflow: hidden;
  transition: all ${props => props.theme.transitions.normal};
  cursor: pointer;
  flex: 0 0 300px;
  width: 300px;

  &:hover {
    transform: translateY(-4px);
  }

  .gallery-image {
    height: 400px;
    background-image: url(${props => props.image});
    background-size: cover;
    background-position: center;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex: 1 1 100%;
    width: 100%;
  }
`;

const OpinionsSection = styled.section`
  padding: ${props => props.theme.spacing.huge} 0;
  background: ${props => props.theme.colors.background};
  position: relative;

  .opinions-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${props => props.theme.spacing.huge};
    position: relative;

    &::before {
      content: 'OPINIONS';
      position: absolute;
      right: -40px;
      top: 50%;
      transform: translateY(-50%);
      writing-mode: vertical-rl;
      text-orientation: mixed;
      font-size: clamp(6rem, 12vw, 14rem);
      font-weight: 900;
      color: #5F8D4E;
      letter-spacing: -0.04em;
      opacity: 1;
      z-index: 0;
    }

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      grid-template-columns: 1fr;

      &::before {
        display: none;
      }
    }
  }
`;

const OpinionCard = styled.div`
  background: transparent;
  padding: 0;
  position: relative;
  z-index: 1;

  p {
    font-size: 1.1rem;
    line-height: 1.4;
    color: ${props => props.theme.colors.text};
    margin-bottom: ${props => props.theme.spacing.xxl};
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .author {
    font-size: 1.5rem;
    font-weight: 900;
    color: ${props => props.theme.colors.text};
    text-transform: uppercase;
    letter-spacing: 1px;
    text-align: right;
  }
`;

const HomePage = () => {
  const { currentUser } = useAuth();
  const [featuredArticles, setFeaturedArticles] = useState([]);

  useEffect(() => {
    const loadArticles = () => {
      const mockArticles = [
        {
          id: 'featured-1',
          title: 'Spartans Dominate Championship Game in Historic Victory',
          excerpt: 'In a thrilling display of teamwork and determination, our Spartans secured their place in school history with an unforgettable championship win.',
          image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800&h=600&fit=crop',
          category: 'Sports',
          author: 'Sarah Johnson',
          date: '2 hours ago',
          featured: true
        }
      ];
      setFeaturedArticles(mockArticles);
    };

    setTimeout(loadArticles, 500);
  }, []);

  const MainFeaturedArticle = featuredArticles.find(a => a.featured) || featuredArticles[0];

  return (
    <HomeContainer>
      <HeroSection>
        <HeroContent>
          <VerticalSchurr>
            <h1>SCHURR</h1>
          </VerticalSchurr>
          
          <HorizontalJournalism>
            <h2>JOURNALISM</h2>
          </HorizontalJournalism>
          
          <VerticalNav>
            <VerticalNavLink to="/news">NEWS</VerticalNavLink>
            <VerticalNavLink to="/sports">SPORTS</VerticalNavLink>
            <VerticalNavLink to="/features">FEATURES</VerticalNavLink>
            <VerticalNavLink to="/opinion">OPINION</VerticalNavLink>
            <VerticalNavLink to="/archive">ARCHIVE</VerticalNavLink>
            <VerticalNavLink to="/search">GALLERY</VerticalNavLink>
            {currentUser && (
              <AdminNavLink to="/admin">ADMIN</AdminNavLink>
            )}
          </VerticalNav>
        </HeroContent>
      </HeroSection>

      <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px' }}>
        <SpartanWeeklySection>
          <SectionHeader>
            <h2>SPARTAN <span>WEEKLY</span></h2>
          </SectionHeader>
          <FeaturedGrid>
            {MainFeaturedArticle && (
              <MainFeatured image={MainFeaturedArticle.image}>
                <div className="featured-image">
                  <div className="category-badge">{MainFeaturedArticle.category}</div>
                </div>
                <div className="featured-content">
                  <h3>{MainFeaturedArticle.title}</h3>
                  <p>{MainFeaturedArticle.excerpt}</p>
                </div>
              </MainFeatured>
            )}
            <VideoSection>
              <div className="video-label">YouTube video</div>
            </VideoSection>
          </FeaturedGrid>
        </SpartanWeeklySection>

        <GallerySection>
          <GalleryArrow className="left">{'<'}</GalleryArrow>
          <GalleryGrid>
            <GalleryItem image="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=600&fit=crop">
              <div className="gallery-image"></div>
            </GalleryItem>
          </GalleryGrid>
          <GalleryArrow className="right">{'>'}</GalleryArrow>
          <SectionHeader>
            <h2>GALLERY</h2>
          </SectionHeader>
        </GallerySection>

        <OpinionsSection>
          <div className="opinions-content">
            <OpinionCard>
              <p>LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM</p>
              <div className="author">JOHN DOE</div>
            </OpinionCard>
            <OpinionCard>
              <p>LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM  LOREM IPSUM</p>
              <div className="author">JOHN DOE</div>
            </OpinionCard>
          </div>
        </OpinionsSection>
      </div>
    </HomeContainer>
  );
};

export default HomePage;